
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from 'sonner';
import { debugLog, debugError } from '@/utils/debugLogger';
import { safeLocalStorage } from '@/utils/safeStorage';

const STORAGE_KEY = 'gildy_pending_email';
const STORAGE_TIMESTAMP_KEY = 'gildy_pending_email_at';
const TTL_HOURS = 24; // Auto-cleanup after 24 hours

interface PendingEmailState {
  pendingEmail: string | null;
  setPendingEmail: (email: string) => void;
  clearPendingEmail: () => void;
  resendConfirmation: () => Promise<boolean>;
  isResending: boolean;
  cooldownSeconds: number;
}

/**
 * Hook to manage pending email confirmation state
 * Stores email in localStorage for users who registered but haven't confirmed email yet
 */
export function usePendingEmailConfirmation(): PendingEmailState {
  const [pendingEmail, setPendingEmailState] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const storedEmail = safeLocalStorage.getItem(STORAGE_KEY);
      const storedTimestamp = safeLocalStorage.getItem(STORAGE_TIMESTAMP_KEY);

      if (storedEmail && storedTimestamp) {
        // Check if TTL has expired
        const timestamp = new Date(storedTimestamp).getTime();
        const now = Date.now();
        const daysPassed = (now - timestamp) / (1000 * 60 * 60 * 24);

        if (daysPassed > (TTL_HOURS / 24)) {
          // TTL expired, clean up
          debugLog('auth', 'Pending email TTL expired, clearing', undefined, 'usePendingEmailConfirmation');
          safeLocalStorage.removeItem(STORAGE_KEY);
          safeLocalStorage.removeItem(STORAGE_TIMESTAMP_KEY);
          setPendingEmailState(null);
        } else {
          debugLog('auth', 'Found pending email in localStorage', undefined, 'usePendingEmailConfirmation');
          setPendingEmailState(storedEmail);
        }
      }
    } catch (error) {
      debugError('auth', 'Error loading pending email from localStorage', error, 'usePendingEmailConfirmation');
    }
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  const setPendingEmail = useCallback((email: string) => {
    if (typeof window === 'undefined') return;

    try {
      safeLocalStorage.setItem(STORAGE_KEY, email, true);
      safeLocalStorage.setItem(STORAGE_TIMESTAMP_KEY, new Date().toISOString(), true);
      setPendingEmailState(email);
      debugLog('auth', 'Pending email saved to localStorage', undefined, 'usePendingEmailConfirmation');
    } catch (error) {
      debugError('auth', 'Error saving pending email to localStorage', error, 'usePendingEmailConfirmation');
    }
  }, []);

  const clearPendingEmail = useCallback(() => {
    if (typeof window === 'undefined') return;

    try {
      safeLocalStorage.removeItem(STORAGE_KEY);
      safeLocalStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      setPendingEmailState(null);
      debugLog('auth', 'Pending email cleared from localStorage', undefined, 'usePendingEmailConfirmation');
    } catch (error) {
      debugError('auth', 'Error clearing pending email from localStorage', error, 'usePendingEmailConfirmation');
    }
  }, []);

  const resendConfirmation = useCallback(async (): Promise<boolean> => {
    if (!pendingEmail) {
      debugLog('auth', 'No pending email to resend confirmation', undefined, 'usePendingEmailConfirmation');
      return false;
    }

    if (cooldownSeconds > 0) {
      toast.error(`Attendi ${cooldownSeconds} secondi prima di reinviare`);
      return false;
    }

    setIsResending(true);
    debugLog('auth', 'Resending confirmation email to:', pendingEmail, 'usePendingEmailConfirmation');

    try {
      // Get current user for userId
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const userId = currentUser?.id;
      
      const { data, error } = await supabase.functions.invoke('post-signup-verify', {
        body: { userId, email: pendingEmail }
      });

      if (error) {
        debugError('auth', 'Error resending confirmation email:', error, 'usePendingEmailConfirmation');
        
        const msg = error.message || '';
        if (msg.includes('rate') || msg.includes('too many')) {
          toast.error('Troppe richieste. Attendi qualche minuto prima di riprovare.');
          setCooldownSeconds(60);
        } else {
          toast.error('Errore durante l\'invio dell\'email. Riprova più tardi.');
        }
        return false;
      }

      toast.success('Email di conferma inviata!', {
        description: 'Controlla la tua casella email (anche spam).'
      });
      
      // Set cooldown to prevent spam
      setCooldownSeconds(30);
      return true;
    } catch (error) {
      debugError('auth', 'Exception resending confirmation email:', error, 'usePendingEmailConfirmation');
      toast.error('Errore durante l\'invio dell\'email');
      return false;
    } finally {
      setIsResending(false);
    }
  }, [pendingEmail, cooldownSeconds]);

  return {
    pendingEmail,
    setPendingEmail,
    clearPendingEmail,
    resendConfirmation,
    isResending,
    cooldownSeconds
  };
}
